'use client';

import Link from 'next/link';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ArrowForwardIcon from '@mui/icons-material/ArrowForwardOutlined';
import Navigation from '../../../components/Navigation';
import {
    DocAnswer,
    DocCode,
    DocEyebrow,
    DocMistakeList,
    DocRelated,
    DocSection,
    DocSubtitle,
    DocText,
    DocTitle,
} from '../../../components/DocArticle';
import { encodeBase64 } from '../../../utils/base64';

const generateUrl = (() => {
    const oldEncoded = encodeBase64(JSON.stringify({ email: 'old@example.com', role: 'user' }, null, 2));
    const newEncoded = encodeBase64(JSON.stringify({ email: 'new@example.com', role: 'user' }, null, 2));
    return `/?old=${oldEncoded}&new=${newEncoded}`;
})();

const mistakes = [
    {
        mistake: 'Sending the patch with the wrong Content-Type',
        fix: 'application/json-patch+json is what tells the server to interpret the body as RFC 6902 operations. With plain application/json many frameworks treat it as a generic JSON body and your operations never run.',
    },
    {
        mistake: 'Using PUT semantics with a PATCH body',
        fix: 'PUT replaces the whole resource; JSON Patch modifies it. Sending a partial document to a PUT endpoint deletes everything you omitted. If the API expects a full document, use JSON Merge Patch (RFC 7396) or PUT - see the comparison guide.',
    },
    {
        mistake: 'No concurrency guard on read-modify-write',
        fix: 'If you GET a resource, change it locally, and PATCH it back, someone else can write in between. Send If-Match with the ETag you received, or include a test operation on a version field.',
    },
    {
        mistake: 'Treating a 4xx as "the patch format is broken"',
        fix: '400 usually means malformed patch JSON, 409 a conflict with current state, 422 an operation that failed against the current document, 412 a failed If-Match. Read the status before debugging the wrong layer.',
    },
];

export default function JsonPatchRestApiPage() {
    return (
        <>
            <Navigation activePage="docs" />
            <main>
                <Container maxWidth="lg" sx={{ py: { xs: 4, sm: 6 }, px: { xs: 2, sm: 3 } }}>
                    {/* Breadcrumb */}
                    <Box component="nav" sx={{ display: 'flex', mb: 4, fontSize: '0.875rem', color: 'var(--fg-muted)' }}>
                        <Link href="/" style={{ textDecoration: 'none', color: 'var(--fg-muted)' }}>
                            <Typography component="span" sx={{ '&:hover': { color: 'var(--accent)' }, fontSize: '0.875rem' }}>Home</Typography>
                        </Link>
                        <Typography component="span" sx={{ mx: 1, fontSize: '0.875rem', color: 'var(--fg-muted)' }}>/</Typography>
                        <Link href="/docs" style={{ textDecoration: 'none', color: 'var(--fg-muted)' }}>
                            <Typography component="span" sx={{ '&:hover': { color: 'var(--accent)' }, fontSize: '0.875rem' }}>Documentation</Typography>
                        </Link>
                        <Typography component="span" sx={{ mx: 1, fontSize: '0.875rem', color: 'var(--fg-muted)' }}>/</Typography>
                        <Typography component="span" sx={{ color: 'var(--fg)', fontSize: '0.875rem' }}>JSON Patch in REST APIs</Typography>
                    </Box>

                    <Box sx={{ maxWidth: '800px' }}>
                        <DocEyebrow>Documentation</DocEyebrow>
                        <DocTitle>JSON Patch in REST APIs</DocTitle>
                        <DocSubtitle>
                            JSON Patch was designed for HTTP: it is the body of a PATCH request that changes part of a
                            resource. Here is the full protocol picture - media types, headers, concurrency, status
                            codes - plus client code that sends it.
                        </DocSubtitle>

                        <DocAnswer>
                            To use JSON Patch against a REST API, send an <strong>HTTP PATCH</strong> request with the
                            header <code>Content-Type: application/json-patch+json</code> and the array of RFC 6902
                            operations as the body. The server applies them in order; if one fails, the whole request
                            fails.
                        </DocAnswer>

                        <DocSection>A complete request</DocSection>
                        <DocCode label="Request">{`PATCH /users/123 HTTP/1.1
Host: api.example.com
Content-Type: application/json-patch+json
If-Match: "v42"

[
  { "op": "test", "path": "/version", "value": 42 },
  { "op": "replace", "path": "/email", "value": "new@example.com" }
]`}</DocCode>
                        <DocText>
                            The body is a JSON Patch document, nothing more. Three headers do the heavy lifting:
                        </DocText>
                        <Box component="ul" sx={{ color: 'var(--fg-muted)', lineHeight: 1.8, pl: 3, mb: 3 }}>
                            <li><code>Content-Type: application/json-patch+json</code> - the media type registered by RFC 6902. This is what routes the body to patch semantics instead of a generic JSON handler.</li>
                            <li><code>If-Match</code> - carries the <code>ETag</code> from your earlier GET, so the request only succeeds if the resource has not changed since.</li>
                            <li><code>Accept-Patch: application/json-patch+json</code> - sent by servers (for example on a GET or an OPTIONS response) to advertise that the resource accepts JSON Patch. Defined by RFC 5789.</li>
                        </Box>

                        <DocSection>Responses and status codes</DocSection>
                        <DocText>
                            Conventions vary between APIs, but these are the common readings:
                        </DocText>
                        <Box component="ul" sx={{ color: 'var(--fg-muted)', lineHeight: 1.8, pl: 3, mb: 3 }}>
                            <li><strong>200</strong> with the updated resource, or <strong>204</strong> with no body - the patch applied.</li>
                            <li><strong>400</strong> - the patch document itself is malformed (bad JSON, missing op/path).</li>
                            <li><strong>409</strong> - the request conflicts with the current state of the resource.</li>
                            <li><strong>412</strong> - your If-Match precondition failed: someone changed the resource first.</li>
                            <li><strong>415</strong> - the server does not accept application/json-patch+json bodies.</li>
                            <li><strong>422</strong> - the patch is well-formed but an operation failed against the current document (a missing path, an out-of-bounds index). Our <Link href="/docs/json-patch-errors" style={{ color: 'var(--accent)' }}>errors guide</Link> maps each of those failures to its fix.</li>
                        </Box>

                        <DocSection>Concurrency: ETag + test</DocSection>
                        <DocText>
                            The dangerous pattern in REST is read-modify-write: GET a resource, edit it locally, PATCH
                            it back - while another client does the same. Two complementary guards:
                        </DocText>
                        <DocText>
                            <strong>If-Match / ETag</strong> works at the HTTP layer: the server rejects the request
                            with 412 when the resource changed. <strong>The{' '}
                            <Link href="/operations/test" style={{ color: 'var(--accent)' }}>test operation</Link></strong>{' '}
                            works inside the patch: it fails the whole operation list when a specific field is not
                            what you expect. Many APIs support both; using either beats using neither.
                        </DocText>

                        <DocSection>Sending a patch from your code</DocSection>
                        <DocCode label="JavaScript (fetch)">{`const response = await fetch('/users/123', {
  method: 'PATCH',
  headers: {
    'Content-Type': 'application/json-patch+json',
    'If-Match': etag,
  },
  body: JSON.stringify([
    { op: 'replace', path: '/email', value: 'new@example.com' },
  ]),
});

if (response.status === 412) {
  // resource changed - re-fetch and rebuild the patch
}`}</DocCode>
                        <DocCode label="Python (requests)">{`import requests

response = requests.patch(
    'https://api.example.com/users/123',
    json=[{ 'op': 'replace', 'path': '/email', 'value': 'new@example.com' }],
    headers={
        'Content-Type': 'application/json-patch+json',
        'If-Match': etag,
    },
)

if response.status_code == 412:
    ...  # re-fetch and rebuild`}</DocCode>
                        <DocText>
                            The patch itself usually comes from a diff between the resource you fetched and your
                            modified copy - see{' '}
                            <Link href="/docs/how-to-generate-json-patch" style={{ color: 'var(--accent)' }}>how to generate a JSON Patch</Link> -
                            and is executed server-side exactly as in{' '}
                            <Link href="/docs/how-to-apply-json-patch" style={{ color: 'var(--accent)' }}>how to apply a JSON Patch</Link>.
                        </DocText>

                        <DocSection>JSON Patch or JSON Merge Patch?</DocSection>
                        <DocText>
                            RFC 7396 Merge Patch is the other PATCH body format: a partial document instead of an
                            operation list, with its own media type (<code>application/merge-patch+json</code>).
                            Many APIs accept both. The trade-offs - arrays, null semantics, test - are in{' '}
                            <Link href="/guides/json-patch-vs-json-merge-patch" style={{ color: 'var(--accent)' }}>JSON Patch vs JSON Merge Patch</Link>.
                        </DocText>

                        <DocSection>Common mistakes</DocSection>
                        <DocMistakeList items={mistakes} />

                        <DocSection>Reference</DocSection>
                        <DocText>
                            HTTP PATCH and Accept-Patch are defined by{' '}
                            <a href="https://datatracker.ietf.org/doc/html/rfc5789" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent)' }}>RFC 5789</a>,
                            the JSON Patch media type by{' '}
                            <a href="https://datatracker.ietf.org/doc/html/rfc6902#section-6" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent)' }}>RFC 6902, section 6</a>,
                            and conditional requests (ETag / If-Match) by{' '}
                            <a href="https://datatracker.ietf.org/doc/html/rfc9110#section-13" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent)' }}>RFC 9110, section 13</a>.
                        </DocText>

                        {/* CTA */}
                        <Box sx={{
                            mt: 6, p: 4, textAlign: 'center',
                            borderRadius: 'var(--radius-lg)',
                            background: 'var(--accent)', color: 'white',
                        }}>
                            <Typography variant="h2" sx={{ fontSize: '1.5rem', fontWeight: 600, mb: 2 }}>
                                Build the patch for your next PATCH call
                            </Typography>
                            <Typography sx={{ mb: 3, opacity: 0.9 }}>
                                Paste the resource as you fetched it and as you want it - the generator emits the exact operation list to send.
                            </Typography>
                            <Button
                                variant="contained"
                                size="large"
                                href={generateUrl}
                                endIcon={<ArrowForwardIcon />}
                                sx={{
                                    backgroundColor: 'white', color: 'var(--accent)', fontWeight: 600,
                                    px: 4, py: 1.5, textTransform: 'none',
                                    '&:hover': { backgroundColor: 'rgba(255,255,255,0.9)' },
                                }}
                            >
                                Open the generator with this example
                            </Button>
                        </Box>

                        <DocRelated links={[
                            { href: '/docs/json-patch-errors', title: 'JSON Patch errors', description: 'What 422-level failures mean and how to fix them' },
                            { href: '/operations/test', title: 'Test operation', description: 'The in-patch concurrency guard' },
                            { href: '/guides/json-patch-vs-json-merge-patch', title: 'JSON Patch vs Merge Patch', description: 'Choosing the PATCH body format' },
                            { href: '/docs/how-to-apply-json-patch', title: 'How to apply a JSON Patch', description: 'What the server does with your request body' },
                        ]} />
                    </Box>
                </Container>
            </main>
        </>
    );
}
